/**
 * EditMemberModal - 기존 회원의 이름·연락처를 수정하는 모달 (instructor-web)
 *
 * admin-web의 동명 모달을 instructor-web 컨벤션(design-system)으로 이식한 것.
 * 강사 전용 `updateInstructorMember`를 호출하므로 강사도 수정 가능.
 * 초기값 prefill + 변경된 필드만 전송.
 */

import { useState, useEffect } from "react";
import type { FormEvent } from "react";
import { Card, Button, Alert } from "@vlab-corporation/design-system";
import { useUpdateMember, type UpdateMemberParams } from "@/hooks/useCreateMember";

interface EditMemberModalProps {
  isOpen: boolean;
  centerId: number;
  memberId: number;
  /** 현재 이름(prefill) */
  initialName: string;
  /** 현재 연락처(prefill, 없으면 빈 문자열) */
  initialMobile: string;
  onClose: () => void;
  onUpdated: () => void;
}

function normalizeMobile(input: string): string {
  return input.replace(/[^0-9]/g, "");
}

function isValidMobile(normalized: string): boolean {
  return normalized.length === 10 || normalized.length === 11;
}

function getErrorMessage(error: unknown): string | null {
  if (error && typeof error === "object" && "message" in error) {
    const msg = (error as { message: unknown }).message;
    if (typeof msg === "string" && msg.length > 0) return msg;
  }
  return null;
}

export function EditMemberModal({
  isOpen,
  centerId,
  memberId,
  initialName,
  initialMobile,
  onClose,
  onUpdated,
}: EditMemberModalProps) {
  const [name, setName] = useState(initialName);
  const [mobile, setMobile] = useState(initialMobile);
  const [validationError, setValidationError] = useState<string | null>(null);

  const updateMember = useUpdateMember();

  useEffect(() => {
    if (isOpen) {
      setName(initialName);
      setMobile(initialMobile);
      setValidationError(null);
      updateMember.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialName, initialMobile]);

  if (!isOpen) return null;

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();

    const trimmedName = name.trim();
    const normalizedMobile = normalizeMobile(mobile);

    if (!trimmedName) {
      setValidationError("이름을 입력해주세요.");
      return;
    }
    if (!normalizedMobile) {
      setValidationError("연락처를 입력해주세요.");
      return;
    }
    if (!isValidMobile(normalizedMobile)) {
      setValidationError("연락처는 숫자 10~11자리로 입력해주세요.");
      return;
    }

    const normalizedInitialMobile = normalizeMobile(initialMobile);
    const params: UpdateMemberParams = { centerId, memberId };
    if (trimmedName !== initialName.trim()) params.name = trimmedName;
    if (normalizedMobile !== normalizedInitialMobile) params.mobile = normalizedMobile;

    if (params.name === undefined && params.mobile === undefined) {
      setValidationError("변경된 내용이 없습니다.");
      return;
    }

    setValidationError(null);
    updateMember.mutate(params, { onSuccess: () => onUpdated() });
  };

  const isSubmitDisabled = !name.trim() || !mobile.trim() || updateMember.isPending;
  const serverErrorMessage = updateMember.isError ? getErrorMessage(updateMember.error) : null;

  const inputClass =
    "w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-primary-400";

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4">
      <Card className="w-full max-w-md p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">회원 정보 수정</h2>

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label
              htmlFor="editMemberName"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              이름 <span className="text-red-500">*</span>
            </label>
            <input
              id="editMemberName"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={inputClass}
              required
            />
          </div>

          <div className="mb-4">
            <label
              htmlFor="editMemberMobile"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              연락처 <span className="text-red-500">*</span>
            </label>
            <input
              id="editMemberMobile"
              type="tel"
              value={mobile}
              onChange={(e) => setMobile(e.target.value)}
              placeholder="예: 010-1234-5678"
              className={inputClass}
              required
            />
          </div>

          {validationError && (
            <Alert variant="error" className="mb-4">
              {validationError}
            </Alert>
          )}

          {updateMember.isError && !validationError && (
            <Alert variant="error" className="mb-4">
              {serverErrorMessage ?? "회원 수정에 실패했습니다. 다시 시도해주세요."}
            </Alert>
          )}

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose}>
              취소
            </Button>
            <Button type="submit" variant="primary" disabled={isSubmitDisabled}>
              {updateMember.isPending ? "저장 중..." : "저장"}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
